import type React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import type {
  AppointmentOptionDto,
  AppointmentPreparationDto,
  TicketAppointmentStateDto,
  TicketRequest,
} from "@/api/types";
import { DateRange, formatDateRange } from "@/components/DateRange/DateRange";
import { AlertTriangleIcon } from "@/components/Icons/AlertTriangleIcon";
import { RadioCard } from "@/components/Radio/RadioCard";
import { Box } from "@/design-system/Box";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useTranslation } from "@/translations";

import { getVisibilityOptionIcon, getVisibilityOptionLabelKey } from "../helpers";
import type { CreateTicketModalFormValues } from "../types";

const LoadingMomentsStepContent = ({ preparation }: { preparation?: AppointmentPreparationDto }): React.ReactNode => {
  const { t } = useTranslation();

  return (
    <Stack gap="lg" align="center">
      <span className="text-center text-body-emphasis">
        {t(
          preparation === "extended"
            ? "page.resident-tickets.create-modal.appointment.loading.title"
            : "page.resident-tickets.create-modal.appointment.loading.quick",
        )}
      </span>
      <FullSizeLoader size="sm" noDelay />
    </Stack>
  );
};

type PickMomentStepContentProps = {
  options: AppointmentOptionDto[];
  selectedSlotId: string | undefined;
  onSelectSlot: (slotId: string) => void;
  isMomentUnavailableShown: boolean;
  onDismissMomentUnavailable: () => void;
};

const PickMomentStepContent = ({
  options,
  selectedSlotId,
  onSelectSlot,
  isMomentUnavailableShown,
  onDismissMomentUnavailable,
}: PickMomentStepContentProps): React.ReactNode => {
  const { t, i18n } = useTranslation();

  return (
    <Stack gap="md">
      <span className="text-body text-text-secondary">
        {t("page.resident-tickets.create-modal.appointment.pick.subtitle")}
      </span>
      {isMomentUnavailableShown && (
        <Notice
          variant="danger"
          icon={<AlertTriangleIcon />}
          message={t("page.resident-tickets.create-modal.appointment.not-available.description")}
          action={{
            label: t("page.resident-tickets.create-modal.appointment.not-available.confirm"),
            onClick: onDismissMomentUnavailable,
          }}
          data-testid="jh-moment-unavailable"
        />
      )}
      <Stack gap="sm">
        {options.map((option) => (
          <RadioCard
            key={option.slotId}
            name="jh-moment"
            value={option.slotId}
            checked={option.slotId === selectedSlotId}
            onChange={() => onSelectSlot(option.slotId)}
            label={formatDateRange(i18n, { start: option.start, end: option.end, format: "shortWithTime" })}
            data-testid="jh-moment-option"
          />
        ))}
      </Stack>
    </Stack>
  );
};

type PreviewStepContentProps = {
  selectedOption: AppointmentOptionDto | undefined;
  planningState: TicketAppointmentStateDto | undefined;
};

const PreviewStepContent = ({ selectedOption, planningState }: PreviewStepContentProps): React.ReactNode => {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const form = useFormContext<CreateTicketModalFormValues>();

  const currTitle = useWatch({ name: "title", control: form.control });
  const currDescription = useWatch({ name: "content", control: form.control });
  const currImages = useWatch({ name: "images", control: form.control });
  const currVisibility = useWatch({ name: "visibility", control: form.control });

  const getVisibilityOptionLabel = (visibility: TicketRequest["visibility"]) => {
    const translationKey = getVisibilityOptionLabelKey(visibility);

    if (visibility === "sharedWithBuilding") {
      return t(translationKey, { buildingName: sessionUser.address?.building?.name || "" });
    }

    return t(translationKey);
  };

  return (
    <Stack gap="lg">
      {selectedOption ? (
        <Stack gap="xs">
          <span className="text-overline-emphasis text-text-secondary">
            {t("page.resident-tickets.create-modal.appointment.preview.expected-arrival")}
          </span>
          <DateRange start={selectedOption.start} end={selectedOption.end} format="shortWithTime" emphasized />
          <span className="text-caption text-text-secondary">
            {t("page.resident-tickets.create-modal.appointment.preview.arrival-note")}
          </span>
        </Stack>
      ) : (
        <Stack gap="xs" data-testid="jh-preview-no-moment">
          <span className="text-heading-4">
            {t("page.resident-tickets.create-modal.appointment.preview.no-moment.title")}
          </span>
          <span className="text-body text-text-secondary">
            {planningState === "failed"
              ? t("page.resident-tickets.create-modal.appointment.preview.no-moment.failed")
              : t("page.resident-tickets.create-modal.appointment.preview.no-moment.description")}
          </span>
        </Stack>
      )}
      <Box radius="md" background="secondary" padding="md">
        <Stack gap="md">
          <Stack gap="xs">
            <h3 className="text-heading-3">{currTitle}</h3>
            <p className="text-body whitespace-pre-line">{currDescription}</p>
          </Stack>
          {currImages.length > 0 && (
            <ol className="flex w-full flex-col gap-space-sm @sm:flex-row">
              {currImages.map((image, index) => (
                <li key={image.url} className="aspect-square overflow-hidden rounded-md @sm:max-h-card-media">
                  <img
                    className="size-full object-cover"
                    src={image.url}
                    alt={t("model.ticket.attachment.alt", { number: index + 1 })}
                  />
                </li>
              ))}
            </ol>
          )}
          <Stack gap="xs">
            <span className="text-overline-emphasis text-text-secondary">
              {t("page.resident-tickets.create-modal.form.visibility")}
            </span>
            <Inline gap="sm" align="center" wrap={false}>
              {getVisibilityOptionIcon(currVisibility)}
              <span className="text-body">{getVisibilityOptionLabel(currVisibility)}</span>
            </Inline>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};

export const CreateTicketModalAppointmentSteps = {
  LoadingMoments: LoadingMomentsStepContent,
  PickMoment: PickMomentStepContent,
  Preview: PreviewStepContent,
};
